import { ColumnFiltersState, createColumnHelper } from '@tanstack/react-table';
import { ReactElement, Fragment, useState, useRef } from 'react';
import styles from './JobHistoryGrid.module.scss';
import { Button, CircularProgress } from '@getgo/chameleon-web-react-wrapper/register';
import { UpdateJobItem } from '../../../windows-updates/models/update-job-item';
import { useTranslation } from 'react-i18next';
import { useUpdateJobs } from '../../../windows-updates/hooks/use-update-jobs-hook';
import { MenuItemClickEvent } from '@getgo/chameleon-web';
import { navigate } from '../../../core/services/navigation.service';
import { clsx } from 'clsx';
import ChameleonGrid, { TableGroupFormatter } from '../../core/chameleon-grid/ChameleonGrid';
import JobUpdateCountCellRenderer from '../../core/chameleon-cell-renderers/JobUpdatesCountCellRenderer';
import { CellText } from '../../core/chameleon-cell-renderers/CellText';
import JobDateScheduledCellRenderer from '../../core/chameleon-cell-renderers/JobDateScheduledCellRenderer';
import DateTimeCellRenderer from '../../core/chameleon-cell-renderers/DateTimeCellRenderer';
import JobHistoryFilterPanel from './JobHistoryFilterPanel';
import { useSearchParams } from 'react-router-dom';
import JobHistoryFilterHeader from './JobHistoryFilterHeader';
import JobTenantCellRenderer from '../../core/chameleon-cell-renderers/JobTenantCellRenderer';
import { tenantChameleonFilterFnForJob } from '../../utils/tenant-chameleon-filterFn';
import Tenant from '../../../core/models/Tenant';
import useGetTenantDevicesAndGroups from '../../../windows-updates/hooks/useGetTenantDevicesAndGroups';
import useGetGlobalSelectedTenants from '../../../windows-updates/hooks/useGetGlobalSelectedTenants';
import { JobStatusProgressCellRenderer } from '../../core/chameleon-cell-renderers/status-cell-renderers/JobStatusProgressCellRenderer';
import { JobStatusCellRenderer } from '../../core/chameleon-cell-renderers/status-cell-renderers/JobStatusCellRenderer';
import OldJobStatusCellRenderer from '../../core/chameleon-cell-renderers/JobStatusCellRenderer';
import { useAppSelector } from '../../../core/store/root.store';
import JobProgressCellRenderer from '../../core/chameleon-cell-renderers/JobProgressCellRenderer';
import { HostRunStatus } from '@goto/remote-execution';
import { selectPartiallySuccessfulHistoryEnabled } from '../../../ui-frame/state/ui-frame.slice';
import { getIsNewJobItem } from '../../../core/utilities/update-job-utils';
import { sortByJobProgress, sortByJobStatus } from '../../utils/column-sort';

const columnHelper = createColumnHelper<UpdateJobItem>();

function JobHistoryGrid(): ReactElement {
  const { t } = useTranslation();

  const partiallySuccessfulHistoryEnabled = useAppSelector(selectPartiallySuccessfulHistoryEnabled);

  const globalTenants = useGetGlobalSelectedTenants();

  const { tenantDevices } = useGetTenantDevicesAndGroups();

  const autocompleteRef = useRef(null);

  const { loading, jobs } = useUpdateJobs(tenantDevices);

  const filteredJobsByGlobalTenants = jobs.filter(job => {
    if (globalTenants.length === 0) return true;
    return (
      job.tenants?.some(tenant => globalTenants.some(gt => gt.uuid === tenant.uuid)) ||
      (job.tenants?.length === 0 && globalTenants.find(tenant => tenant.uuid === 'items-without-tenant'))
    );
  });

  const [searchParams] = useSearchParams();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    ...(searchParams.get('jobStatus') ? [{ id: 'jobStatus', value: searchParams.getAll('jobStatus') }] : [])
  ]);

  const [selectedTenants, setSelectedTenants] = useState<Tenant[]>([]);

  const seeDetails = (event: React.MouseEvent | MenuItemClickEvent, data: UpdateJobItem | undefined): void => {
    event.preventDefault();
    navigate({ path: '/updates/:updateJobId', params: { updateJobId: `${data?.id}` } });
  };

  const columns = [
    ...(partiallySuccessfulHistoryEnabled
      ? [
          columnHelper.accessor('progress', {
            header: () => t('jobHistoryGrid.headers.progress'),
            cell: props =>
              JobStatusProgressCellRenderer({
                jobRunStatistics: props.row?.original?.jobRunStatistics,
                jobStatus: props.row.original.jobStatus,
                isNewJob: getIsNewJobItem(props.row.original)
              }),
            sortingFn: sortByJobProgress,
            filterFn: (row, id, filterValue) => {
              return filterValue.length > 0 ? filterValue.includes(row.original.jobStatus) : true;
            }
          }),
          columnHelper.accessor('detailedStatus', {
            header: () => t('jobHistoryGrid.headers.status'),
            cell: props =>
              JobStatusCellRenderer({
                isOsUpdate: !!props.row.original.osUpdateInfo,
                jobStatus: props.row.original.jobStatus,
                jobRunStatistics: props.row?.original?.jobRunStatistics
              }),
            sortingFn: sortByJobStatus,
            filterFn: (row, id, filterValue) => {
              return filterValue.length > 0 ? filterValue.includes(row.original.jobStatus) : true;
            }
          })
        ]
      : [
          columnHelper.accessor('hostRunStatusCounts', {
            header: () => t('jobHistoryGrid.headers.hostRunStatusCounts'),
            cell: props => JobProgressCellRenderer(props.getValue()),
            filterFn: (row, id, filterValue) => {
              return filterValue.length > 0
                ? row.original.hostRunStatusCounts.some(
                    hrsc => hrsc.count > 0 && filterValue.includes(HostRunStatus[hrsc.status].toLowerCase())
                  )
                : true;
            }
          })
        ]),
    columnHelper.accessor('displayName', {
      header: () => t('jobHistoryGrid.headers.displayName'),
      cell: props => <CellText>{props.getValue()}</CellText>
    }),
    columnHelper.accessor('tenants', {
      header: () => t('jobHistoryGrid.headers.tenants'),
      cell: props => JobTenantCellRenderer(props.row.original, tenantDevices),
      filterFn: row => {
        return tenantChameleonFilterFnForJob(row.original, selectedTenants, tenantDevices);
      }
    }),
    ...(partiallySuccessfulHistoryEnabled
      ? []
      : [
          columnHelper.accessor('status', {
            header: () => t('jobHistoryGrid.headers.status'),
            cell: props => OldJobStatusCellRenderer(props.getValue())
          })
        ]),
    columnHelper.accessor('updateCount', {
      header: () => t('jobHistoryGrid.headers.updateCount'),
      cell: props =>
        JobUpdateCountCellRenderer({
          osMandatoryUpdateCount: props.row.original?.osUpdateInfo?.mandatoryUpdateCount ?? 0,
          osOptionalUpdateCount: props.row.original?.osUpdateInfo?.optionalUpdateCount ?? 0,
          appUpdateCount: props.row.original?.appUpdateInfo?.count ?? 0
        }),
      filterFn: (row, id, filterValue) => {
        if (filterValue.length === 0) {
          return true;
        }

        if (filterValue.includes('os') && row.original.osUpdateInfo) {
          return (
            row.original.osUpdateInfo.mandatoryUpdateCount > 0 || row.original.osUpdateInfo.optionalUpdateCount > 0
          );
        }
        if (filterValue.includes('app') && row.original.appUpdateInfo) {
          return row.original.appUpdateInfo.count > 0;
        }
        return false;
      }
    }),
    columnHelper.accessor('deviceCount', {
      header: () => t('jobHistoryGrid.headers.deviceCount'),
      cell: props => <CellText>{props.getValue()}</CellText>
    }),
    columnHelper.accessor('scheduledAt', {
      header: () => t('jobHistoryGrid.headers.scheduledAt'),
      cell: props => JobDateScheduledCellRenderer(props.getValue())
    }),
    columnHelper.accessor('finishedAt', {
      header: () => t('jobHistoryGrid.headers.finishedAt'),
      cell: props => DateTimeCellRenderer(props.getValue()),
      minSize: 70
    }),
    columnHelper.display({
      id: 'actions',
      minSize: 0,
      maxSize: 0,
      meta: { isPlaceholder: true },
      cell: cellContext => (
        <div className={clsx(styles.actions)}>
          <div className={clsx(styles.actionItemsWrapper)}>
            <div className={clsx(styles.actionItems)}>
              <Button variant="neutral" size="large" onClick={e => seeDetails(e, cellContext.row.original)}>
                {t('jobHistoryGrid.seeDetails')}
              </Button>
            </div>
          </div>
        </div>
      )
    })
  ];

  const tableGroupFormatter: TableGroupFormatter = grouping => {
    function isSpecificGroup(group: string): group is 'completed' | 'running' | 'scheduled' {
      return ['completed', 'running', 'scheduled'].includes(group);
    }

    if (grouping && isSpecificGroup(grouping)) {
      return t(`jobHistoryGrid.groups.${grouping}`);
    } else {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress></CircularProgress>
      </div>
    );
  }

  return (
    <Fragment>
      <ChameleonGrid
        classes={{ row: styles.row }}
        defaultColumn={{ size: undefined }}
        columns={columns}
        data={loading ? [] : filteredJobsByGlobalTenants}
        totalRowCount={filteredJobsByGlobalTenants.length}
        tableGroupFormatter={tableGroupFormatter}
        manualSorting={false}
        manualPagination={false}
        manualFiltering={false}
        selectionEnabled={false}
        globalFilterEnabled={true}
        groupBy={'jobStatus'}
        columnFilters={columnFilters}
        columnVisibility={{ jobStatus: false }}
        initialState={{
          sorting: [
            { id: 'jobStatus', desc: true },
            { id: 'finishedAt', desc: true }
          ]
        }}
        renderSlot={
          <>
            <JobHistoryFilterHeader
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setIsFilterPanelOpen={setIsFilterPanelOpen}
              autocompleteRef={autocompleteRef}></JobHistoryFilterHeader>
            <JobHistoryFilterPanel
              filtersState={columnFilters}
              isOpen={isFilterPanelOpen}
              onClose={() => setIsFilterPanelOpen(false)}
              onFiltersChange={setColumnFilters}
              setSelectedTenants={setSelectedTenants}
              autocompleteRef={autocompleteRef}
              globalTenants={globalTenants}></JobHistoryFilterPanel>
          </>
        }
      />
    </Fragment>
  );
}

export default JobHistoryGrid;
